import React, { useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import loadable from '@loadable/component'
import balanceText from 'balance-text'
import { throttle } from 'throttle-debounce'
import { ReactHTMLConverter } from 'react-html-converter'

const PlanTest = loadable(() => import('../../components/PlanTest/PlanTest'))
const BMICalculator = loadable(() =>
  import('../../components/BMICalculator/BMICalculator')
)
const PremiumButton = loadable(() =>
  import('../../components/PremiumButton/PremiumButton')
)
const CalorieBurnCalculator = loadable(() =>
  import('../../components/CalorieBurnCalculator/CalorieBurnCalculator')
)
const CalorieIntakeCalculator = loadable(() =>
  import('../../components/CalorieIntakeCalculator/CalorieIntakeCalculator')
)
const CalorieCounter = loadable(() =>
  import('../../pages/FeaturesPage/_CalorieCounter')
)
const HabitTrackers = loadable(() =>
  import('../../pages/FeaturesPage/_HabitTrackers')
)
const Recipes = loadable(() => import('../../pages/FeaturesPage/_Recipes'))
const AppButtons = loadable(() =>
  import('../../components/AppButtons/AppButtons')
)

// eslint-disable-next-line react/prop-types
function Widget({ children }) {
  return process.env.NODE_ENV === 'test' ? (
    <div>{children}</div>
  ) : (
    widgets[children]
  )
}

// eslint-disable-next-line react/prop-types
function Fact({ children }) {
  return (
    <aside className="c-fact">
      <h2 className="c-fact__heading">Did you know...?</h2>
      <p className="c-fact__text">{children}</p>
    </aside>
  )
}

const converter = new ReactHTMLConverter()
converter.registerComponent('widget', Widget)
converter.registerComponent('fact', Fact)

const JoinTheStudyCTA = () => (
  <div className="o-grid o-grid--center">
    <div className="o-grid">
      <a
        href={'https://p.proofpilot.com/#/s/Lifesum-learn-and-feel'}
        className="c-button c-button--premium  c-premium-button__cta mt-4 mb-40"
      >
        <span>Join the study</span>
      </a>
    </div>
  </div>
)

const widgets = {
  PlanTest: <PlanTest />,
  PremiumButton: <PremiumButton />,
  AppStoreButtons: <AppButtons />,
  BMICalculator: <BMICalculator />,
  CalorieCounter: <CalorieCounter />,
  HabitTrackers: <HabitTrackers />,
  Recipes: <Recipes />,
  CalorieBurnCalculator: <CalorieBurnCalculator />,
  CalorieIntakeCalculator: <CalorieIntakeCalculator />,
  JoinTheStudy: <JoinTheStudyCTA />,
}

const widgetRegExp = new RegExp(
  `\\[(${Object.keys(widgets).join('|')})\\]`,
  'g'
)

function updateVideoProportions() {
  return throttle(250, () => {
    for (let video of document.querySelectorAll(
      'iframe[src*="youtube.com"][width][height]'
    )) {
      const parentBox = video && video.parentNode.getBoundingClientRect()
      const ratio = video.getAttribute('height') / video.getAttribute('width')
      video.setAttribute('width', parentBox.width)
      video.setAttribute('height', parseInt(parentBox.width * ratio, 10))
    }
  })
}

function CMSContent({ content, fact, dataUid }) {
  const decoratedContent = useMemo(() => {
    function addFact(content) {
      if (!fact) return content
      let firstGoodPlace = content.search(/<\/(h2|h3)>/) + 5
      if (!firstGoodPlace) {
        firstGoodPlace = content.indexOf('</p>') + 4
      }

      return (
        content.slice(0, firstGoodPlace) +
        `<Fact>${fact}</Fact>` +
        content.slice(firstGoodPlace)
      )
    }

    return converter.convert(
      addFact(content).replace(widgetRegExp, '<Widget>$1</Widget>')
    )
  }, [content, fact])

  useEffect(() => {
    balanceText('.s-text h1', { watch: true })
    window.addEventListener('resize', updateVideoProportions)

    return () => {
      window.removeEventListener('resize', updateVideoProportions)
    }
  }, [content])

  return <div data-uid={dataUid}>{decoratedContent}</div>
}

CMSContent.propTypes = {
  content: PropTypes.oneOfType([PropTypes.array, PropTypes.string]).isRequired,
  fact: PropTypes.string,
  dataUid: PropTypes.string,
}

export default CMSContent
